import { WindowPresetId } from '@cutr/constants/windows/presets';
import {
  AreaId,
  resolveAreaConfigurations,
  resolveFrameBeamWidthsMM,
  resolveSystem,
  WindowSpec,
} from '@cutr/constants/windows/windows';
import {
  getAreas,
  getFrameDimensions,
  getInnerBeams,
} from '@cutr/constants/windows/windows-geometry.ts';
import React from 'react';
import { Layer, Stage } from 'react-konva';
import { useParams } from 'react-router-dom';

import { usePositionStore } from '@/windows/api/positionStore';
import { useWindowStore } from '@/windows/api/windowStore';

import { Areas } from './Areas';
import { BottomCaptions, LeftCaptions } from './DimensionCaptions';
import { BottomInputPanel, LeftInputPanel } from './DimensionPanel';
import { events, useEditorEvents } from './events';
import { Frame } from './frames/Frame';
import { KonvaProvider, useKonva } from './KonvaContext';
import { getOffsets, RenderStyles } from './renderStyles';
import { useRenderStyles } from './renderStyles';
import styles from './WindowEditor.module.css';
import { useWindowEditor, WindowEditorProvider } from './WindowEditorContext';

const CONTAINER_ID = 'window-viewer-container';

export const WindowEditor = ({
  windowSpec,
  presetId,
  readonly = false,
}: {
  windowSpec: WindowSpec;
  presetId: WindowPresetId;
  readonly?: boolean;
}) => {
  const { positionId } = useParams();
  const { selectedAreaId } = usePositionStore();
  const editorStyles = useRenderStyles('editor');

  const getInitialDimensions = () => {
    const parent = getParent(CONTAINER_ID);
    if (!parent) return { stageScale: 1, stageWidth: 0, stageHeight: 0 };

    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;

    const { frameWidthMM, frameHeightMM } = getFrameDimensions(
      windowSpec.frameShape
    );

    const { stageScale, stageWidth, stageHeight } = getStageDimensions(
      frameWidthMM,
      frameHeightMM,
      parentWidth,
      parentHeight,
      editorStyles
    );

    return {
      stageScale,
      stageWidth,
      stageHeight,
    };
  };

  const { stageScale, stageWidth, stageHeight } = getInitialDimensions();

  return (
    <KonvaProvider
      containerId={CONTAINER_ID}
      key={`${positionId}-konvaProvider`}
      stageScaleX={stageScale}
      stageScaleY={stageScale}
      stageWidth={stageWidth}
      stageHeight={stageHeight}
      renderStyles={editorStyles}
    >
      <WindowEditorProvider
        windowSpec={windowSpec}
        presetId={presetId}
        readonly={readonly}
        selectedAreaId={selectedAreaId}
      >
        <AutoScaleWindowEditor />
      </WindowEditorProvider>
    </KonvaProvider>
  );
};

const AutoScaleWindowEditor = () => {
  const { frameWidthMM, frameHeightMM } = useWindowEditor();

  useEditorEvents();
  useStageResizeObserver({ frameWidthMM, frameHeightMM });

  return <WindowRenderer />;
};

const WindowRenderer = () => {
  const {
    stageRef,
    stageScaleX,
    stageScaleY,
    stageWidth,
    stageHeight,
    containerId,
    renderStyles,
  } = useKonva();
  const { bottomMM, topMM } = getOffsets(renderStyles);
  const { frameHeightMM, frameX, frameY, windowSpec, selectedAreaId } =
    useWindowEditor();
  const { configurations: positionConfigurations } = usePositionStore();
  const { configurations: orderConfigurations } = useWindowStore();

  const ref = React.useRef<HTMLDivElement>(null);

  const onResize = () => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();

    el.style.setProperty(
      '--containerHeight',
      `${window.innerHeight - rect.top}px`
    );
  };

  React.useEffect(() => {
    onResize();
    const root = document.querySelector('#root');
    if (!root) return;

    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(root);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onAreaSelect = (areaId: AreaId) => {
    stageRef.current?.fire(
      events.areaSelected,
      {
        areaId,
      },
      true
    );
  };

  if (!windowSpec) return null;

  const frameBeamWidthsMM = resolveFrameBeamWidthsMM({
    orderConfigurations,
    positionConfigurations,
  });

  const systemClass = resolveSystem({
    orderConfigurations,
    positionConfigurations,
  });

  return (
    <div className={styles.container} ref={ref}>
      <Stage
        ref={stageRef}
        id={containerId}
        scaleY={-stageScaleY}
        scaleX={stageScaleX}
        width={stageWidth}
        height={stageHeight}
        offsetY={frameHeightMM + bottomMM + topMM}
      >
        <Layer>
          <Frame
            frame={windowSpec.frameShape}
            x={frameX}
            y={frameY}
            innerBeams={getInnerBeams(windowSpec)}
            frameBeamWidthsMM={frameBeamWidthsMM}
          />
          {stageHeight > 0 && <DimensionPanels />}
          <Areas
            x={frameX}
            y={frameY}
            windowSpec={windowSpec}
            areas={getAreas(windowSpec)}
            areaConfigurations={resolveAreaConfigurations({
              orderConfigurations,
              positionConfigurations,
              areaConfigurations: windowSpec.areaConfigurations,
            })}
            frameBeamWidthsMM={frameBeamWidthsMM}
            systemClass={systemClass?.value}
            selectedAreaId={selectedAreaId}
            onAreaSelect={onAreaSelect}
          />
        </Layer>
      </Stage>
    </div>
  );
};

const DimensionPanels = () => {
  const { readonly, frameWidthMM, frameHeightMM, windowSpec, frameX, frameY } =
    useWindowEditor();

  if (!windowSpec) return null;

  if (readonly) {
    return (
      <>
        <LeftCaptions
          frameHeightMM={frameHeightMM}
          frameY={frameY}
          windowSpec={windowSpec}
        />
        <BottomCaptions
          frameWidthMM={frameWidthMM}
          frameX={frameX}
          windowSpec={windowSpec}
        />
      </>
    );
  }

  return (
    <>
      <LeftInputPanel />
      <BottomInputPanel />
    </>
  );
};

const useStageResizeObserver = ({
  frameWidthMM,
  frameHeightMM,
}: {
  frameWidthMM: number;
  frameHeightMM: number;
}) => {
  const { setStageDimensions, containerId, renderStyles } = useKonva();

  React.useLayoutEffect(() => {
    const parent = getParent(containerId);

    if (!parent) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const updateStageDimensions = (
        newParentWidth: number,
        newParentHeight: number
      ) => {
        const { stageScale, stageWidth, stageHeight } = getStageDimensions(
          frameWidthMM,
          frameHeightMM,
          newParentWidth,
          newParentHeight,
          renderStyles
        );

        setStageDimensions({
          stageWidth,
          stageHeight,
          stageScaleX: stageScale,
          stageScaleY: stageScale,
        });
      };
      const entry = entries[0];
      if (!entry) return;

      const newWidth = entry.contentRect.width;
      const newHeight = entry.contentRect.height;

      updateStageDimensions(newWidth, newHeight);
    });

    resizeObserver.observe(parent);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    frameWidthMM,
    frameHeightMM,
    containerId,
    setStageDimensions,
    renderStyles,
  ]);
};

const getStageDimensions = (
  frameWidthMM: number,
  frameHeightMM: number,
  parentWidth: number,
  parentHeight: number,
  renderStyles: RenderStyles
) => {
  const { leftMM, rightMM, topMM, bottomMM } = getOffsets(renderStyles);
  const totalWidth = frameWidthMM + leftMM + rightMM;
  const totalHeight = frameHeightMM + topMM + bottomMM;

  const stageScale = Math.min(
    parentWidth / totalWidth,
    parentHeight / totalHeight
  );

  return {
    stageScale,
    stageWidth: parentWidth,
    stageHeight: parentHeight,
  };
};

const getParent = (containerId: string) => {
  const container = document.querySelector<HTMLDivElement>(`#${containerId}`);
  if (!container) return;

  const parent = container.parentElement;
  if (!parent) return;

  return parent;
};
